import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ENVIRONMENT } from "../../config.js";

const Error = ({ error, resetErrorBoundary }) => {
  const navigate = useNavigate();
  const isDev = ENVIRONMENT !== "production";

  const baseDelay = 30000; // 30 seconds
  const retryTimeout = useRef(null);
  const countdownInterval = useRef(null);

  const getRetryDelay = () => {
    const delay = parseInt(localStorage.getItem("error-retry-delay"), 10);
    return isNaN(delay) ? baseDelay : delay;
  };

  const setNextRetryDelay = () => {
    const current = getRetryDelay();
    localStorage.setItem("error-retry-delay", current * 2);
  };

  const resetRetryDelay = () => {
    localStorage.removeItem("error-retry-delay");
  };

  const [remaining, setRemaining] = useState(getRetryDelay() / 1000);

  const handleRetry = () => {
    resetRetryDelay();
    resetErrorBoundary();
    navigate("/");
  };

  useEffect(() => {
    const delay = getRetryDelay();

    // Timer countdown
    countdownInterval.current = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Retry trigger
    retryTimeout.current = setTimeout(() => {
      setNextRetryDelay();
      handleRetry();
    }, delay);

    return () => {
      clearTimeout(retryTimeout.current);
      clearInterval(countdownInterval.current);
    };
  }, []);

  return (
    <div style={styles.wrapper}>
      <div style={styles.container}>
        <div style={styles.icon}>🚨</div>
        <h1 style={styles.title}>Oops! Something went wrong.</h1>
        <p style={styles.subtitle}>
          Retrying in <strong>{remaining}</strong> seconds...
        </p>

        {isDev && error && (
          <details style={styles.details}>
            <summary style={styles.summary}>Error Details</summary>
            <pre>{error.message}</pre>
            <pre>{error.stack}</pre>
          </details>
        )}

        <button onClick={handleRetry} style={styles.button}>
          Go Home & Retry
        </button>
      </div>
    </div>
  );
};


const styles = {
  wrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#fff5f5",
  },
  container: {
    backgroundColor: "#fff",
    padding: "2rem",
    borderRadius: "0.75rem",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
    textAlign: "center",
    maxWidth: "500px",
    width: "100%",
    color: "#b91c1c",
  },
  icon: {
    fontSize: "3rem",
    marginBottom: "1rem",
  },
  title: {
    fontSize: "1.75rem",
    fontWeight: "bold",
    marginBottom: "0.5rem",
  },
  subtitle: {
    fontSize: "1rem",
    marginBottom: "1.5rem",
    color: "#7f1d1d",
  },
  details: {
    textAlign: "left",
    backgroundColor: "#fee2e2",
    padding: "1rem",
    borderRadius: "0.5rem",
    marginBottom: "1rem",
    fontSize: "0.85rem",
    overflow: "auto",
  },
  summary: {
    cursor: "pointer",
    marginBottom: "0.5rem",
    fontWeight: "bold",
  },
  button: {
    backgroundColor: "#dc2626",
    color: "#fff",
    padding: "0.5rem 1rem",
    borderRadius: "0.375rem",
    border: "none",
    cursor: "pointer",
    fontWeight: "500",
  },
};

export default Error;
